import * as React from 'react'
import { connect } from 'react-redux'
import * as ReactStrap from 'reactstrap'
import * as Common from '../common'
import * as IsActive from '../../components/is_active'
import * as Actions from './actions'
import { WrappedTopicPreferences } from './reducer'
import * as queryString from 'query-string'
import * as History from 'history'

interface DispatchProps {
  onTopicPreferenceChange: (id: string) => void;
  addCustomTopics: (topicNames: string[]) => void;
  submitTopicPreferences: (communityIdentifier: Common.CommunityIdentifier, token: string, topics: Actions.TopicPreference[]) => void;
  fetchUserTopics: (communityIdentifier: Common.CommunityIdentifier, token: string) => void;
  fetchSuggestedTopics: (communityIdentifier: Common.CommunityIdentifier, token: string) => void;
}

// No need for OwnProps as router data comes from Redux state
type Props = DispatchProps & WrappedTopicPreferences;

const topicCardStyle = {
  transition: 'all 0.3s ease',
  borderRadius: '8px',
  border: '1px solid #dee2e6',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
  height: '100%',
};

const topicCardActiveStyle = {
  ...topicCardStyle,
  borderColor: '#7FAE1B',
  backgroundColor: '#f8f9fa',
};

const topicCardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '10px',
  paddingBottom: '10px',
  borderBottom: '1px solid #e9ecef',
};

const topicCardTitleStyle = {
  margin: 0,
  fontWeight: 500,
  color: '#343a40',
};

const TopicCard = ({ topic, onChange }: { topic: Actions.TopicPreference; onChange: (id: string) => void }) => (
  <ReactStrap.Col md="4" sm="6" className="mb-4">
    <ReactStrap.Card style={topic.isActive ? topicCardActiveStyle : topicCardStyle}>
      <ReactStrap.CardBody>
        <div style={topicCardHeaderStyle}>
          <h5 style={topicCardTitleStyle}>{topic.name}</h5>
          <IsActive.UncontrolledToggleButton isActive={topic.isActive} onChange={() => onChange(topic.id)} />
        </div>
      </ReactStrap.CardBody>
    </ReactStrap.Card>
  </ReactStrap.Col>
);

const sectionStyle = {
  marginBottom: '2.5rem',
  padding: '1.5rem',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
};

const sectionHeaderStyle = {
  marginBottom: '1.5rem',
};

const sectionDescriptionStyle = {
  color: '#6c757d',
  marginBottom: '1.5rem',
  lineHeight: 1.5,
};

const topicsContainerStyle = {
  marginTop: '1rem',
};

const saveButtonContainerStyle = {
  marginTop: '2rem',
  textAlign: 'center' as const,
};

const saveButtonStyle = {
  padding: '0.75rem 2rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
};

const LoadingCard = () => (
  <ReactStrap.Col md="4" sm="6" className="mb-4">
    <ReactStrap.Card style={topicCardStyle}>
      <ReactStrap.CardBody>
        <div style={topicCardHeaderStyle}>
          <div style={{ width: '80%', height: '1.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
          <div style={{ width: '40px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '34px' }}></div>
        </div>
      </ReactStrap.CardBody>
    </ReactStrap.Card>
  </ReactStrap.Col>
);

class TopicPreferences extends React.Component<Props> {
  componentDidMount() {
    // Epic handles the API calls automatically when route is entered
  }
  
  handleAddCustomTopics = (inputValue: string) => {
    if (!inputValue || !inputValue.trim()) {
      return;
    }
    
    // Parse comma-separated topics and clean them
    const topicNames = inputValue
      .split(',')
      .map(topic => topic.trim())
      .filter(topic => topic.length > 0);
    
    if (topicNames.length > 0) {
      this.props.addCustomTopics(topicNames);
    }
  };
  
  render() {
    const { topicPreferences, onTopicPreferenceChange, submitTopicPreferences } = this.props;
    
    // URL parameters are now handled by the epic and stored in state
    const token = topicPreferences.token || '';
    const communityIdentifier = topicPreferences.identifier || '';
    
    return (
  <ReactStrap.Container style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
    <ReactStrap.Row>
      <ReactStrap.Col>
        <Common.PublicHeader configurations={topicPreferences.configurations} />
        <header className="page-header text-center" style={{ marginBottom: '1.5rem' }}>Topic Preferences</header>
      </ReactStrap.Col>
    </ReactStrap.Row>
    <ReactStrap.Row>
      <ReactStrap.Col sm="12">
        <div className="lead-text text-center" style={{ color: '#6c757d', marginBottom: '2rem', lineHeight: 1.6, fontSize: '1.1rem' }}>
          <span>Personalize your newsletter by selecting topics that interest you.</span>
        </div>
      </ReactStrap.Col>
    </ReactStrap.Row>

    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
      <div className="d-flex align-items-center mb-2">
        <span className="mr-2" role="img" aria-label="info" style={{ fontSize: '1.2rem' }}>ℹ️</span>
        <span style={{ fontWeight: 500 }}>How it works:</span>
      </div>
      <p className="mb-0">Toggle the switch next to each topic to customize your content. When a topic is active, you'll receive related content in your newsletter.</p>
    </div>
    
    {topicPreferences.errored && (
      <ReactStrap.Alert color="danger" className="mb-4">
        {topicPreferences.errorMessage || 'An error occurred. Please try again.'}
      </ReactStrap.Alert>
    )}
    
    {topicPreferences.submitted && !topicPreferences.is_submitting && !topicPreferences.errored && (
      <ReactStrap.Alert color="success" className="mb-4">
        Your topic preferences have been saved successfully!
      </ReactStrap.Alert>
    )}
    

    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <header className="section-header-grey">Your Topics</header>
        <div style={sectionDescriptionStyle}>
          These are the topics you are currently subscribed to:
        </div>
        
        {/* Add custom topics input */}
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
          <ReactStrap.Label style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#495057' }}>Add Custom Topics</ReactStrap.Label>
          <ReactStrap.Input
            type="text"
            placeholder="Enter topics separated by commas (e.g., Technology, Sports, Health)"
            style={{ marginBottom: '0.5rem' }}
            onKeyPress={(e: any) => {
              if (e.key === 'Enter') {
                this.handleAddCustomTopics(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <small className="text-muted">Press Enter to add topics to your list</small>
        </div>
      </div>
      <ReactStrap.Row style={topicsContainerStyle}>
        {topicPreferences.isLoadingTopics ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : topicPreferences.topics.length > 0 ? (
          topicPreferences.topics.map((topic) => (
            <TopicCard key={topic.id} topic={topic} onChange={onTopicPreferenceChange} />
          ))
        ) : (
          <ReactStrap.Col>
            <p className="text-center text-muted">You haven't subscribed to any topics yet. Check out our recommendations below.</p>
          </ReactStrap.Col>
        )}
      </ReactStrap.Row>
    </div>

    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <header className="section-header-grey">Recommended Topics</header>
        <div style={sectionDescriptionStyle}>
          Explore these topics to enhance your content experience:
        </div>
      </div>
      <ReactStrap.Row style={topicsContainerStyle}>
        {topicPreferences.isLoadingSuggestions ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : topicPreferences.recommendedTopics.length > 0 ? (
          topicPreferences.recommendedTopics.map((topic) => (
            <TopicCard key={topic.id} topic={topic} onChange={onTopicPreferenceChange} />
          ))
        ) : (
          <ReactStrap.Col>
            <p className="text-center text-muted">No topic recommendations available at the moment.</p>
          </ReactStrap.Col>
        )}
      </ReactStrap.Row>
    </div>

    <div style={saveButtonContainerStyle}>
      {topicPreferences.is_submitting ? (
        <Common.BouncingBalls />
      ) : (
        <ReactStrap.Button
          color="primary"
          style={{ 
            ...saveButtonStyle,
            backgroundColor: Common.keyValue(topicPreferences.configurations, 'primary_color', '#58c4c7'),
            borderColor: Common.keyValue(topicPreferences.configurations, 'primary_color', '#58c4c7')
          }}
          disabled={!topicPreferences.isDirty}
          onClick={() => {
            // Only send user topics (all of them, active and inactive for weight updates)
            // and selected recommended topics (to add them as new preferences)
            const selectedRecommendedTopics = topicPreferences.recommendedTopics.filter(topic => topic.isActive);
            const topicsToSubmit = [...topicPreferences.topics, ...selectedRecommendedTopics];
            submitTopicPreferences(communityIdentifier, token, topicsToSubmit);
          }}
        >
          Save Preferences
        </ReactStrap.Button>
      )}
    </div>
  </ReactStrap.Container>
    );
  }
}

const mapStateToProps = ({ topicPreferences }: WrappedTopicPreferences) => ({ topicPreferences });

const mapDispatchToProps = {
  onTopicPreferenceChange: Actions.handleTopicPreferenceChange,
  addCustomTopics: Actions.addCustomTopics,
  submitTopicPreferences: Actions.submitTopicPreferences,
  fetchUserTopics: Actions.fetchUserTopics,
  fetchSuggestedTopics: Actions.fetchSuggestedTopics,
};

export const TopicPreferencesConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(TopicPreferences);